import React, { useRef, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import { XMarkIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import { VariantReader } from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"
import modalStack from "./ModalStack.js"

/**
 * Modal Component
 *
 * Animation is opt-in via the variant's `animation` field:
 *   animation: {
 *     open:  { initial: {...}, animate: {...}, transition: {...} },
 *     close: { exit: {...},    transition: {...} }
 *   }
 * Without `animation`, the modal opens and closes instantly.
 *
 * @param {boolean} passInteractionEvents - When true, allows interaction events (scroll, hover, etc.)
 *                                         to pass through the modal overlay to content behind it.
 */
const Modal = ({
  isOpen,
  onClose,
  onCloseBtnClick,
  title,
  children,
  closeOnClickOutside = false,
  allowCloseBtn = true,
  className = "",
  zIndex = 400,
  closeOnEscape = false,
  headerContent = null,
  footerContent = null,
  variant = "default",
  styleOverrides,
  passInteractionEvents = false,
}) => {
  const modalRef = useRef()
  const [isClosing, setIsClosing] = useState(false)

  // Generate unique modal ID (persists across re-renders)
  const modalId = useRef(`modal-${Math.random().toString(36).substring(2, 9)}-${Date.now()}`).current

  // Read variant styles — `animation` (if present) is a plain object and passes through untouched
  const variants = VariantReader.useVariant({
    componentName: "Modal",
    variant,
    styleOverrides,
  })

  // animation is opt-in via variant config
  const animation = variants?.animation ?? null
  const closeDuration = animation ? (animation.close?.transition?.duration ?? 0) * 1000 : 0

  const handleClose = () => {
    if (!onClose) return
    if (!animation) {
      onClose()
      return
    }
    setIsClosing(true)
    setTimeout(() => {
      setIsClosing(false)
      onClose()
    }, closeDuration)
  }

  // Track if this modal is registered in the stack
  const [isRegistered, setIsRegistered] = useState(false)

  useEffect(() => {
    if (isOpen && !isRegistered) {
      modalStack.push(modalId, handleClose, closeOnEscape)
      setIsRegistered(true)
    } else if (!isOpen && isRegistered) {
      modalStack.remove(modalId)
      setIsRegistered(false)
    }
  }, [isOpen, isRegistered, modalId, closeOnEscape])

  useEffect(() => {
    return () => {
      if (isRegistered) modalStack.remove(modalId)
    }
  }, [modalId, isRegistered])

  useEffect(() => {
    if (isOpen && isRegistered) {
      const modalInStack = modalStack.getStack().find(m => m.id === modalId)
      if (modalInStack) modalInStack.closeOnEscape = closeOnEscape
    }
  }, [closeOnEscape, isOpen, isRegistered, modalId])

  useEffect(() => {
    if (!isOpen) return
    const scrollY = window.scrollY
    const paddingRight = document.body.style.paddingRight
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.position = "fixed"
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = "100%"
    document.body.style.paddingRight = `${scrollbarWidth}px`
    return () => {
      document.body.style.position = ""
      document.body.style.top = ""
      document.body.style.width = ""
      document.body.style.paddingRight = paddingRight
      window.scrollTo(0, scrollY)
    }
  }, [isOpen])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!closeOnClickOutside || !modalRef.current) return
      if (modalRef.current.contains(event.target)) return
      // Don't close if the click landed inside any other open modal/overlay
      if (event.target.closest("[data-modal]")) return
      handleClose()
    }
    if (isOpen && closeOnClickOutside) document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen, closeOnClickOutside])

  // Build framer-motion props — empty objects = no animation
  const motionProps = animation
    ? {
        initial: animation.open?.initial ?? {},
        animate: isClosing ? (animation.close?.exit ?? {}) : (animation.open?.animate ?? {}),
        exit: animation.close?.exit ?? {},
        transition: isClosing
          ? (animation.close?.transition ?? { duration: 0 })
          : (animation.open?.transition ?? { duration: 0 }),
      }
    : { initial: {}, animate: {}, exit: {}, transition: { duration: 0 } }

  const modalContent = (
    <div
      className={variants.overlay}
      style={{ zIndex, display: "flex", pointerEvents: passInteractionEvents ? "none" : "auto" }}
    >
      <AnimatePresence>
        {(isOpen || isClosing) && (
          <motion.div
            ref={modalRef}
            data-modal
            className={twMerge(variants.container, footerContent ? "flex flex-col" : "", className)}
            style={{ pointerEvents: "auto" }}
            {...motionProps}
          >
            {/* Header */}
            <div className={variants.header}>
              {allowCloseBtn && (
                <button
                  className={variants.closeButton}
                  onClick={() => {
                    onCloseBtnClick && onCloseBtnClick()
                    handleClose()
                  }}
                >
                  <XMarkIcon className={variants.closeIcon} />
                </button>
              )}
              <Typography component="h1" className={variants.title}>
                {title}
              </Typography>
              {headerContent && (
                <div className={variants.headerContent}>{headerContent}</div>
              )}
            </div>

            {/* Content */}
            {footerContent ? (
              <div className="flex flex-col flex-1 overflow-hidden">
                <div className={twMerge(
                  "flex-1 overflow-y-auto",
                  variant.includes("admin")
                    ? "scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper"
                    : "scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper"
                )}>
                  <div className={variants.content}>{children}</div>
                </div>
                <div className={twMerge(variants.footer, "sticky bottom-0 z-10")}>
                  {footerContent}
                </div>
              </div>
            ) : (
              <div className={variants.contentWrapper}>
                <div className={variants.content}>{children}</div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )

  return typeof document !== "undefined" && (isOpen || isClosing)
    ? createPortal(modalContent, document.body)
    : null
}

// Expose modalStack globally for debugging in development
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.modalStack = modalStack
}

export default Modal
